import React, { useEffect, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { format, isBefore, startOfDay } from 'date-fns';
import { Calendar, Bell, ChevronLeft, Play } from 'lucide-react';
import { Modal, PrimaryButton, ClipCard } from '@/components/ui';
import { ClipExperienceHistory } from '@/components/ClipExperienceHistory';
import { CATEGORIES, COLORS } from '@/data';
import { generateId, now, getRedGifsThumbnail } from '@/lib/utils';
import type { Clip, ScheduledActivity, CouplePreference, ActivityActor, TriedClip } from '@/types';
import 'react-day-picker/dist/style.css';
import { RedGifsImage } from '@/components/RedGifsImage';

interface ScheduleActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  clips: Clip[];
  preferences?: CouplePreference[];
  initialDate?: Date;
  onSchedule: (activity: ScheduledActivity) => void;
  context?: ActivityActor;
  coupleId?: string;
  tried?: TriedClip[];
}

type Step = 'select-clip' | 'select-date' | 'confirm';

export const ScheduleActivityModal: React.FC<ScheduleActivityModalProps> = ({
  isOpen,
  onClose,
  clips,
  preferences = [],
  initialDate,
  onSchedule,
  context,
  coupleId,
  tried = [],
}) => {
  const [step, setStep] = useState<Step>('select-clip');
  const [selectedClip, setSelectedClip] = useState<Clip | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);
  const [notes, setNotes] = useState('');
  const [reminder, setReminder] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  // The modal stays mounted, so take the caller's date each time it opens
  useEffect(() => {
    if (isOpen) setSelectedDate(initialDate);
  }, [isOpen, initialDate]);

  // Get clips user wants to try based on preferences
  const tryClipIds = preferences
    .filter(p => p.rating === 'try')
    .map(p => p.clipId);

  const filteredClips = categoryFilter
    ? clips.filter(c => c.categoryId === categoryFilter)
    : clips;

  const handleSelectClip = (clip: Clip) => {
    setSelectedClip(clip);
    // A day was already chosen (e.g. tapped on a calendar), so go straight to confirm
    setStep(selectedDate ? 'confirm' : 'select-date');
  };

  // DayPicker reports undefined when the already-selected day is tapped again; treat that as picking it
  const handleSelectDate = (date: Date | undefined, triggerDate?: Date) => {
    const picked = date ?? triggerDate;
    if (picked && !isBefore(picked, startOfDay(new Date()))) {
      setSelectedDate(picked);
      setStep('confirm');
    }
  };

  const handleSchedule = () => {
    if (!selectedClip || !selectedDate) return;

    const activity: ScheduledActivity = {
      id: generateId('sched'),
      clipId: selectedClip.id,
      scheduledDate: format(selectedDate, 'yyyy-MM-dd'),
      createdAt: now(),
      notes: notes.trim() || undefined,
      reminder,
      createdBy: context,
    };

    onSchedule(activity);
    handleClose();
  };

  const handleClose = () => {
    setStep('select-clip');
    setSelectedClip(null);
    setSelectedDate(initialDate);
    setNotes('');
    setReminder(false);
    setCategoryFilter(null);
    onClose();
  };

  const handleBack = () => {
    if (step === 'select-date') {
      setStep('select-clip');
      setSelectedClip(null);
    } else if (step === 'confirm') {
      setStep('select-date');
    }
  };

  // Custom styles for dark theme calendar
  const customStyles = `
    .schedule-modal .rdp {
      --rdp-cell-size: 40px;
      --rdp-accent-color: ${COLORS.lime};
      --rdp-background-color: rgba(212, 255, 31, 0.15);
      margin: 0;
    }
    .schedule-modal .rdp-caption_label {
      font-size: 0.875rem;
      font-weight: 600;
      color: white;
    }
    .schedule-modal .rdp-nav_button {
      color: #a1a1aa;
      width: 28px;
      height: 28px;
      border-radius: 6px;
    }
    .schedule-modal .rdp-nav_button:hover {
      background: #3f3f46;
      color: white;
    }
    .schedule-modal .rdp-head_cell {
      color: #71717a;
      font-size: 0.7rem;
      font-weight: 500;
    }
    .schedule-modal .rdp-day {
      width: 100%;
      height: 40px;
      border-radius: 8px;
      color: #e4e4e7;
      font-size: 0.8rem;
    }
    .schedule-modal .rdp-day:hover:not(.rdp-day_disabled):not(.rdp-day_selected) {
      background: #3f3f46;
    }
    .schedule-modal .rdp-day_today {
      border: 2px solid ${COLORS.lime};
      font-weight: 600;
    }
    .schedule-modal .rdp-day_selected {
      background: ${COLORS.lime} !important;
      color: #18181b !important;
      font-weight: 600;
    }
    .schedule-modal .rdp-day_disabled {
      color: #3f3f46;
      cursor: not-allowed;
    }
  `;

  const category = selectedClip
    ? CATEGORIES.find(c => c.id === selectedClip.categoryId)
    : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        step === 'select-clip' ? 'Choose a Clip' :
        step === 'select-date' ? 'Pick a Date' :
        'Confirm Schedule'
      }
      size="lg"
    >
      <style>{customStyles}</style>
      <div className="schedule-modal">
        {/* Step 1: Select Clip */}
        {step === 'select-clip' && (
          <div className="p-4">
            {/* Category filter */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setCategoryFilter(null)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  !categoryFilter ? 'text-zinc-900' : 'bg-zinc-700 text-zinc-400'
                }`}
                style={!categoryFilter ? { backgroundColor: COLORS.lime } : {}}
              >
                All
              </button>
              {tryClipIds.length > 0 && (
                <button
                  onClick={() => setCategoryFilter('__try__')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    categoryFilter === '__try__' ? 'bg-orange-500/20 text-orange-400 border border-orange-500' : 'bg-zinc-700 text-zinc-400'
                  }`}
                >
                  Want to Try
                </button>
              )}
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setCategoryFilter(cat.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    categoryFilter === cat.id ? 'text-zinc-900' : 'bg-zinc-700 text-zinc-400'
                  }`}
                  style={categoryFilter === cat.id ? { backgroundColor: COLORS.lime } : {}}
                >
                  {cat.emoji} {cat.name}
                </button>
              ))}
            </div>

            {/* Clips list */}
            <div className="max-h-[50vh] overflow-y-auto space-y-2">
              {(categoryFilter === '__try__'
                ? clips.filter(c => tryClipIds.includes(c.id))
                : filteredClips
              ).map(clip => (
                <ClipCard
                  key={clip.id}
                  clip={clip}
                  onClick={() => handleSelectClip(clip)}
                  variant="list"
                />
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Select Date */}
        {step === 'select-date' && selectedClip && (
          <div className="p-4">
            <button
              onClick={handleBack}
              className="flex items-center gap-1 text-zinc-400 hover:text-white text-sm mb-4"
            >
              <ChevronLeft className="w-4 h-4" /> Change clip
            </button>

            {/* Selected clip preview */}
            <div className="bg-zinc-900 rounded-lg p-3 mb-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
                {getRedGifsThumbnail(selectedClip.link) ? (
                  <RedGifsImage
                    url={selectedClip.link}
                    alt={selectedClip.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Play className="w-5 h-5" style={{ color: COLORS.lime }} />
                  </div>
                )}
              </div>
              <div>
                <p className="text-white font-medium text-sm">{selectedClip.title}</p>
                <p className="text-zinc-500 text-xs">{category?.emoji} {category?.name}</p>
              </div>
            </div>

            {/* Calendar */}
            <DayPicker
              mode="single"
              selected={selectedDate}
              onSelect={handleSelectDate}
              disabled={{ before: new Date() }}
              showOutsideDays
            />
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 'confirm' && selectedClip && selectedDate && (
          <div className="p-4">
            <button
              onClick={handleBack}
              className="flex items-center gap-1 text-zinc-400 hover:text-white text-sm mb-4"
            >
              <ChevronLeft className="w-4 h-4" /> Change date
            </button>

            {/* Summary */}
            <div className="bg-zinc-900 rounded-xl p-4 mb-4 border border-zinc-700">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{category?.emoji}</span>
                <div>
                  <p className="text-white font-medium">{selectedClip.title}</p>
                  <p className="text-zinc-400 text-sm">{category?.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-zinc-300">
                <Calendar className="w-4 h-4" style={{ color: COLORS.lime }} />
                <span className="font-medium">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
              </div>
            </div>

            {/* Experience History (if couple has done this before) */}
            {coupleId && (
              <ClipExperienceHistory
                clipId={selectedClip.id}
                coupleId={coupleId}
                tried={tried}
                compact={true}
              />
            )}

            {/* Notes */}
            <div className="mb-4">
              <label className="block text-zinc-400 text-sm mb-2">Add a note (optional)</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Any special plans for the night?"
                className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none"
              />
            </div>

            {/* Reminder toggle */}
            <label className="flex items-center gap-3 bg-zinc-800 rounded-xl p-4 mb-4 cursor-pointer">
              <div
                className={`w-10 h-6 rounded-full transition-colors flex items-center ${
                  reminder ? 'bg-lime-500' : 'bg-zinc-600'
                }`}
              >
                <div
                  className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    reminder ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium flex items-center gap-2">
                  <Bell className="w-4 h-4" /> Remind me
                </p>
                <p className="text-zinc-500 text-sm">Get a notification on the day</p>
              </div>
            </label>

            <PrimaryButton onClick={handleSchedule} className="w-full">
              <Calendar className="w-4 h-4" /> Schedule Activity
            </PrimaryButton>
          </div>
        )}
      </div>
    </Modal>
  );
};
